import axios from "axios";
import { setLocale } from "../utils/localeManager.js";
import {
  FIELD_VALUES_CACHE_KEY,
  clearFieldValuesCache
} from "../utils/fieldValues.js";
import tokenService from "../utils/tokenService";

let latestUserRequestVersion = 0;
let latestUserRequest;

const followLatestUserRequest = (resolve, reject) => {
  const version = latestUserRequestVersion;
  latestUserRequest.then(
    response => {
      if (version !== latestUserRequestVersion) {
        followLatestUserRequest(resolve, reject);
      } else {
        resolve(response);
      }
    },
    error => {
      if (version !== latestUserRequestVersion) {
        followLatestUserRequest(resolve, reject);
      } else {
        reject(error);
      }
    }
  );
};

export const login = ({ dispatch, commit }, user) => {
  return new Promise((resolve, reject) => {
    commit("AUTH_REQUEST");
    axios({
      method: "post",
      url: "/users/login",
      data: {
        user: user
      }
    })
      .then(resp => {
        if (resp.data.user && !resp.data.errors) {
          dispatch("setup_user", resp.data);
          window.Bus.$emit("flash-message", {
            message_type: "success",
            text: "You've successfully logged in"
          });
          resolve(resp);
        } else if (resp.data.idp_url) {
          window.location = resp.data.idp_url;
        } else {
          reject(resp.data.errors);
        }
      })
      .catch(err => {
        commit("AUTH_ERROR");
        tokenService.clearAuth();
        reject(err.response?.data?.errors || "Login failed");
      });
  });
};

export const setup_user = ({ commit }, data) => {
  const token = data.user.token;
  const user = data.user;

  // Use tokenService to validate and store token
  const tokenStored = tokenService.setToken(token);

  if (!tokenStored) {
    commit("AUTH_ERROR");
    window.Bus.$emit(
      "error-message",
      "Invalid authentication token received. Please try again."
    );
    return;
  }

  // Set impersonation flag if needed
  if (data.impersonating) {
    tokenService.setImpersonation(true);
  }

  // Set axios default header
  axios.defaults.headers.common["Authorization"] = "Token token=" + token;
  clearFieldValuesCache();

  commit("AUTH_SUCCESS", {
    token: token,
    user: user
  });

  try {
    const key = `login_count_${user.id}`;
    const next = (parseInt(localStorage.getItem(key), 10) || 0) + 1;
    localStorage.setItem(key, String(next));
  } catch (e) { /* localStorage unavailable - nothing to do here */ }

  if (data.mfaRequired) {
    commit("MFA_REQUIRED", true);
  } else if (user.tfaSecret) {
    commit("TFA", user.tfaSecret);
  } else {
    if (user.collaborateAdmin) {
      commit("COLLABORATE");
    } else if (user.operator) {
      commit("OPERATOR");
    } else if (user.basicNeedsSpecialist) {
      commit("BASIC_NEEDS_SPECIALIST");
    } else if (user.changeover) {
      commit("CHANGEOVER");
    } else if (user.profileIncomplete) {
      commit("INCOMPLETE");
    } else {
      commit("DASHBOARD");
    }
  }
};

export const logout = ({ commit }) => {
  return new Promise(resolve => {
    axios({
      method: "post",
      url: "/users/logout"
    }).finally(() => {
      commit("LOGOUT");
      tokenService.clearAuth();
      delete axios.defaults.headers.common["Authorization"];

      window.Bus.$emit("flash-message", {
        text: "You have successfully logged out."
      });

      location.href = "/login";
      resolve();
    });
  });
};

export const get_user = ({ commit }) => {
  const requestVersion = ++latestUserRequestVersion;
  const request = new Promise((resolve, reject) => {
    // Check token validity before making request
    if (!tokenService.isAuthenticated()) {
      tokenService.clearAuth();
      commit("USER_REQUEST_ERROR");
      reject({ response: { status: 401 } });
      return;
    }

    axios({
      method: "get",
      url: "/user"
    })
      .then(resp => {
        if (requestVersion !== latestUserRequestVersion) {
          // Callers read the shared user state after this promise resolves.
          // Wait for the latest refresh instead of returning an old snapshot.
          followLatestUserRequest(resolve, reject);
          return;
        }

        if (resp.data.user && !resp.data.errors) {
          setLocale(resp.data.user.locale);
          const user = resp.data.user;
          commit("USER_REQUEST_SUCCESS", { user: user });
          if (resp.data.mfaRequired) {
            commit("MFA_REQUIRED", true);
          }
          if (user.profileIncomplete) {
            commit("INCOMPLETE");
          }
          // Sync user timezone with browser timezone (non-providers only — providers set their own)
          const user_tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (!user.provider && user_tz !== user.timezone) {
            const fd = new FormData();
            fd.append("timezone", user_tz);
            axios
              .patch("/user/timezone", fd, { headers: {"Content-Type": "multipart/form-data"} })
              .then(() => {
                if (requestVersion === latestUserRequestVersion) {
                  commit("UPDATE_TIMEZONE", user_tz);
                }
              })
              .catch(() => {});
          }
          resolve(resp);
        } else {
          reject(resp.data.errors);
        }
      })
      .catch(err => {
        const status = err.response?.status;
        if (status === 401 || status === 419 || status === 440) {
          tokenService.clearAuth();
          commit("USER_REQUEST_ERROR");
          reject(err.response);
        } else if (requestVersion !== latestUserRequestVersion) {
          followLatestUserRequest(resolve, reject);
        } else {
          reject(err);
        }
      });
  });
  latestUserRequest = request;
  return request;
};

export const get_session_settings = ({ commit, dispatch }) => {
  return new Promise((resolve, reject) => {
    axios
      .get("/uisessionsettings")
      .then(resp => {
        commit("SESSION_SETTINGS", resp.data);

        if (Object.keys(resp.data).length > 0) {
          const isActive = resp.data.active || false,
            timeoutInactive = resp.data.session_timeout_inactivity * 60000,
            timeoutWarningMessage = resp.data.session_timeout_warning_message,
            warningInterval = resp.data.session_timeout_warning_interval * 1000;
          let activityTimeout, warningTimeout;

          const logoutUser = () => {
            tokenService.setSessionTimedOut(true);
            dispatch("logout");
          };

          const showWarning = () => {
            const warningMessage = timeoutWarningMessage.replace(
              "[X]",
              warningInterval / 1000
            );
            window.Bus.$emit(
              "error-message",
              `${warningMessage} Click OK to stay logged in`,
              "Are you still there?"
            );
            warningTimeout = setTimeout(logoutUser, warningInterval);
          };

          const resetActivityTimeout = () => {
            clearTimeout(activityTimeout);
            clearTimeout(warningTimeout);

            // Check if token is still valid
            if (!tokenService.isAuthenticated()) {
              logoutUser();
              return;
            }

            activityTimeout = setTimeout(
              showWarning,
              timeoutInactive - warningInterval
            );
          };

          const startActivityListeners = () => {
            window.addEventListener("scroll", resetActivityTimeout);
            window.addEventListener("click", resetActivityTimeout);
            window.addEventListener("keydown", resetActivityTimeout);
          };

          if (isActive && timeoutInactive) {
            startActivityListeners();
            activityTimeout = setTimeout(
              showWarning,
              timeoutInactive - warningInterval
            );
          }
        }
        resolve(resp);
      })
      .catch(err => {
        console.log("session_settings err", err);
        reject(err.response?.data?.errors || "Failed to get session settings");
      });
  });
};

export const get_optional_settings = ({ commit }) => {
  return new Promise((resolve, reject) => {
    axios
      .get("/optionalsettings")
      .then(resp => {
        commit("OPTIONAL_SETTINGS", resp.data);
        resolve(resp);
      })
      .catch(err => {
        reject(err.response?.data?.errors || "Failed to get optional settings");
      });
  });
};

export const register = ({ commit }, formData) => {
  return new Promise((resolve, reject) => {
    commit("REGISTER_REQUEST");
    axios
      .post("/users", formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      })
      .then(resp => {
        if (resp.data.user && !resp.data.errors) {
          const token = resp.data.user.token;
          const user = resp.data.user;

          // Validate and store token
          const tokenStored = tokenService.setToken(token);

          if (!tokenStored) {
            commit("REGISTER_ERROR");
            reject(["Invalid token received"]);
            return;
          }

          axios.defaults.headers.common["Authorization"] =
            "Token token=" + token;
          clearFieldValuesCache();
          commit("REGISTER_SUCCESS", { token: token, user: user });
          window.Bus.$emit("flash-message", {
            text: "Welcome to Uwill!"
          });
          resolve(resp);
        } else {
          reject(resp.data.errors);
        }
      })
      .catch(err => {
        commit("REGISTER_ERROR");
        tokenService.clearAuth();
        if (err.response) {
          reject(err.response.data.errors);
        }
      });
  });
};

// Keep other actions unchanged
export const toggle_vacation = ({ commit }) => {
  commit("TOGGLE_VACATION");
};

export const update_timezone = ({ commit }, data) => {
  commit("UPDATE_TIMEZONE", data);
};

export const set_therapists = ({ commit }, therapists) => {
  commit("THERAPIST_RESULTS", { therapists: therapists });
};

export const set_providers = ({ commit }, providers) => {
  commit("PROVIDER_RESULTS", { providers: providers });
};

export const set_invitation_code = ({ commit }, data) => {
  commit("invitation_code", data);
};

export const updateCallState = ({ commit }, newCallState) => {
  commit("UPDATE_CALL_STATE", newCallState);
};
export const openInvitationModal = ({ commit }, value) => {
  commit("INVITATION_MODAL", value);
};
export const find_providers = ({ commit, state }, options = {}) => {
  return new Promise((resolve, reject) => {
    const criteria = state.providerSearchCriteria || {};
    const {
      excluded_ids = null,
      expanded_timeframe = false,
      clinical_referral = null,
      show_more = null,
      basic_needs,
      parent_search = null
    } = options;

    let user_age = null;
    if (state.user && state.user.birthdate) {
      const today = new Date();
      const birthDate = new Date(state.user.birthdate);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }
      user_age = age;
    }

    const timeframe = (expanded_timeframe || basic_needs)
      ? "Sometime within the next few weeks"
      : criteria.how_soon;

    // Map ethnicities to their stored indices; focus_areas are sent as strings.
    const json = localStorage.getItem(FIELD_VALUES_CACHE_KEY);
    const fieldValues = json ? JSON.parse(json) : {};

    let focus_areas = [];
    if (Array.isArray(criteria.focus_areas)) {
      focus_areas = criteria.focus_areas.filter(item =>
        (fieldValues.focus_areas || []).includes(item)
      );
    }

    let ethnicities = [];
    if (Array.isArray(criteria.ethnicities)) {
      // Polish both sides so we tolerate either the raw value ("Black Or African American")
      // from FindProvider's v-select or the display text ("Black or African American")
      // from ProviderMatches' DropdownSelect. Keep this in sync with UserDashboard.vue
      // where ethnicity_choices is built.
      const polish = e => e.replace("Or", "or");
      const ethnicityList = (fieldValues.ethnicities || []).map(polish);
      ethnicities = criteria.ethnicities.map(item =>
        ethnicityList.indexOf(polish(item))
      );
    }

    let supported_identities = [];
    let supported_faiths = [];
    if (criteria.supported_groups != undefined) {
      const identity_list = fieldValues.clinical_populations_served || [];
      const faith_list = fieldValues.clinical_faith_traditions_served || [];
      supported_identities = criteria.supported_groups.filter(text =>
        identity_list.some(i => i.text === text)
      ).map(text => identity_list.find(i => i.text === text).value);
      supported_faiths = criteria.supported_groups.filter(text =>
        faith_list.some(i => i.text === text)
      ).map(text => faith_list.find(i => i.text === text).value);
    }

    commit("PROVIDER_SEARCH_LOADING", true);
    commit("PROVIDER_SEARCH_ERROR", null);

    axios({
      method: "post",
      url: "/findproviders",
      data: {
        kind: criteria.kind,
        gender: criteria.gender,
        how_soon: timeframe,
        ethnicities: ethnicities,
        languages: criteria.languages,
        focus_areas: focus_areas,
        excluded_ids: excluded_ids,
        clinical_referral: clinical_referral,
        show_more: show_more,
        user_age: user_age,
        basic_needs: basic_needs,
        clinical_populations_served: supported_identities,
        clinical_faith_traditions_served: supported_faiths,
        parent_search: parent_search,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }
    })
      .then(resp => {
        if (resp && resp.data) {
          let providers = resp.data.providers;
          if (show_more) {
            providers = state.providers.concat(providers);
          }
          commit("PROVIDER_SEARCH_RESULTS", {
            providers: providers || [],
            alternateProviders: resp.data.alternateProviders || null,
            suggestionDivider: resp.data.goodResults,
            category: resp.data.category,
            role: resp.data.role,
            basicNeeds: resp.data.basicNeeds,
            searchId:
              options.show_more && !resp.data.searchId
                ? state.providerSearchResults?.searchId
                : resp.data.searchId,
            noMoreResults: resp.data.noMoreResults,
            capReached: !!resp.data.capReached
          });
        }
        commit("PROVIDER_SEARCH_LOADING", false);
        resolve(resp && resp.data);
      })
      .catch(err => {
        commit("PROVIDER_SEARCH_ERROR", err);
        commit("PROVIDER_SEARCH_LOADING", false);
        reject(err);
      });
  });
};
export const set_provider_search_criteria = ({ commit }, criteria) => {
  commit("PROVIDER_SEARCH_CRITERIA", criteria);
};
